import { has } from 'lodash';
import moment from 'moment';
import { AgGridFilterModel, FilterUIModel } from './Filters/filterUI.model';
import { CommaSeparatedNumberFilterValue } from './agGridFilterParams';

export class GridHelper {
    /**
     * Projects a FilterUIModel (the domain filter shape) into the flat model that ag-grid v33+
     * built-in handlers accept, or null when there is nothing to project (no filter values).
     * Idempotent: safe to call on models that already contain the native fields.
     **/
    static toAgGridFilterModel(model: FilterUIModel): AgGridFilterModel | null {
        const base = (model as FilterUIModel & { _wsFilter?: FilterUIModel })?._wsFilter ?? model;
        if (!base || !base.filterValues || !base.filterValues.length) {
            return null;
        }
        // Date filter models use ag-grid's native DateFilterModel shape (filterType: 'date',
        // dateFrom/dateTo in YYYY-MM-DD), not the generic filter/filterTo fields used by
        // text/number filters (see #585056 — this was previously mislabeled 'number', which
        // ag-grid's built-in date comparison silently could not interpret, so date filters
        // never actually filtered anything).
        const isDate = base.filterType === 'date';
        if (base.filterValues.length > 1) {
            return {
                ...base,
                operator: base.filterConditionType === Core.FilterConditionTypeEnum.Or ? 'OR' : 'AND',
                conditions: base.filterValues.map((v) =>
                    isDate
                        ? {
                              filterType: base.filterType,
                              type: this.toAgGridFilterType(base.filterType, v.filterType?.displayKey),
                              dateFrom: this.toAgGridDateValue(v.filterValue),
                              dateTo: this.toAgGridDateValue(v.filterToValue),
                          }
                        : {
                              filterType: base.filterType,
                              type: this.toAgGridFilterType(base.filterType, v.filterType?.displayKey),
                              filter: this.toAgGridFilterValue(
                                  base.filterType,
                                  v.filterValue,
                                  v.filterType?.displayKey,
                              ),
                              filterTo: this.toAgGridFilterValue(
                                  base.filterType,
                                  v.filterToValue,
                                  v.filterType?.displayKey,
                              ),
                          },
                ),
            };
        }
        const v = base.filterValues[0];
        if (isDate) {
            return {
                ...base,
                type: this.toAgGridFilterType(base.filterType, v?.filterType?.displayKey),
                dateFrom: this.toAgGridDateValue(v?.filterValue),
                dateTo: this.toAgGridDateValue(v?.filterToValue),
            };
        }
        return {
            ...base,
            type: this.toAgGridFilterType(base.filterType, v?.filterType?.displayKey),
            filter: this.toAgGridFilterValue(base.filterType, v?.filterValue, v?.filterType?.displayKey),
            filterTo: this.toAgGridFilterValue(base.filterType, v?.filterToValue, v?.filterType?.displayKey),
        };
    }

    private static toAgGridDateValue(value?: string): string | undefined {
        if (value === null || value === undefined || value.trim() === '') {
            return undefined;
        }

        const parsed = moment(value, ['MM/DD/YYYY', 'M/D/YYYY', 'MM-DD-YYYY', 'M-D-YYYY'], true);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
    }

    private static toAgGridFilterType(filterType: string, displayKey?: string): string {
        if (filterType !== 'number') {
            return displayKey;
        }

        switch (displayKey) {
            case 'greaterThanOrEqualTo':
                return 'greaterThanOrEqual';
            case 'lessThanOrEqualTo':
                return 'lessThanOrEqual';
            default:
                return displayKey;
        }
    }

    private static toAgGridFilterValue(
        filterType: string,
        value?: string,
        displayKey?: string,
    ): string | number | undefined {
        if (filterType !== 'number' || value === null || value === undefined || value.trim() === '') {
            return value;
        }

        if ((displayKey === 'Comma Separated' || displayKey === 'CommaSeparated') && value.indexOf(',') !== -1) {
            return new CommaSeparatedNumberFilterValue(value) as unknown as number;
        }

        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? value : numericValue;
    }

    public static getFilterColumns<T>(filterModel, propertyMap: Compliance.NameValuePair<T>[]): Core.FilterModel<T>[] {
        const columns: Core.FilterModel<T>[] = [];

        if (filterModel) {
            for (const prop in filterModel) {
                if (has(filterModel, prop)) {
                    const property = this.getProperty(prop, propertyMap);
                    if (property !== null) {
                        // In ag-grid v33+, built-in handler filter names return a wrapper with
                        // _wsFilter containing the real FilterUIModel. Unwrap it if present.
                        const rawFilter = filterModel[prop];
                        const filter = rawFilter?._wsFilter ?? rawFilter;
                        columns.push({
                            filterProperty: property,
                            filterConditionType: filter && filter.filterConditionType,
                            filterValues:
                                filter &&
                                filter.filterValues &&
                                filter.filterValues.map((x) => {
                                    return {
                                        filterType: this.getFilterType(x.filterType.displayKey),
                                        filterValue: this.getFilterValue(x),
                                        filterToValue:
                                            this.getFilterType(x.filterType.displayKey) === Core.FilterTypeEnum.InRange
                                                ? x.filterToValue
                                                : null,
                                    };
                                }),
                        });
                    }
                }
            }
        }

        return columns;
    }

    static getSortModel<T>(
        sortModel: {
            colId: string;
            sort: string;
        }[],
        propertyMap: Compliance.NameValuePair<T>[],
    ): Core.SortModel<T>[] {
        const columns: Core.SortModel<T>[] = [];

        if (sortModel) {
            sortModel.forEach((x) => {
                const property = this.getProperty(x.colId, propertyMap);
                if (property !== null) {
                    columns.push({
                        sortProperty: property,
                        sortDirection: this.getSortDirection(x.sort),
                    });
                }
            });
        }
        return columns;
    }

    static getFilterType(agFilterType: string): Core.FilterTypeEnum {
        switch (agFilterType) {
            case 'equals':
                return Core.FilterTypeEnum.Equals;
            case 'notEqual':
                return Core.FilterTypeEnum.NotEquals;
            case 'contains':
                return Core.FilterTypeEnum.Contains;
            case 'notContains':
                return Core.FilterTypeEnum.NotContains;
            case 'startsWith':
                return Core.FilterTypeEnum.StartsWith;
            case 'endsWith':
                return Core.FilterTypeEnum.EndsWith;
            case 'greaterThan':
                return Core.FilterTypeEnum.GreaterThan;
            case 'greaterThanOrEqual':
            case 'greaterThanOrEqualTo':
                return Core.FilterTypeEnum.GreaterThanOrEqualTo;
            case 'lessThan':
                return Core.FilterTypeEnum.LessThan;
            case 'lessThanOrEqual':
            case 'lessThanOrEqualTo':
                return Core.FilterTypeEnum.LessThanOrEqualTo;
            case 'inRange':
                return Core.FilterTypeEnum.InRange;
            case 'blank':
            case 'Blank':
                return Core.FilterTypeEnum.Blank;
            case 'notBlank':
            case 'Not Blank':
                return Core.FilterTypeEnum.NotBlank;
            case 'yes':
                return Core.FilterTypeEnum.Yes;
            case 'no':
                return Core.FilterTypeEnum.No;
            case ' ':
                return Core.FilterTypeEnum.NoFilter;
            case 'CommaSeparated':
            case 'Comma Separated':
                return Core.FilterTypeEnum.CommaSeparated;
            case 'smartSearch':
            case 'Smart Search':
                return Core.FilterTypeEnum.SmartSearch;
            default:
                return null;
        }
    }

    static getFilterValue(agFilter): string {
        switch (agFilter.filterType) {
            case 'date':
                return agFilter.dateFrom;
            default:
                return agFilter.filterValue;
        }
    }

    static getProperty<T>(prop: string, propertyMap: Compliance.NameValuePair<T>[]): any {
        const property = propertyMap.find((x) => x.name === prop);

        if (!property) {
            return null;
        }

        return property.value;
    }

    static getSortDirection(sort: string): Core.SortDirectionEnum {
        return sort === 'desc' ? Core.SortDirectionEnum.Descending : Core.SortDirectionEnum.Ascending;
    }
}
